import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ORDER_TRANSITIONS } from '@crm/shared'
import type { OrderDetailDto, OrderStatus } from '@crm/shared'
import { updateOrderStatus } from '../../../shared/api/orders.api'
import { toast } from '../../../shared/ui/toastStore'
import { formatMoney } from '../orderFormat'

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { error?: { message?: string } } } }
  return err.response?.data?.error?.message ?? 'Не удалось сменить статус заказа'
}

/**
 * Контекстные кнопки ручной смены статуса заказа. Кандидаты фильтруются по
 * ЕДИНОЙ матрице переходов ORDER_TRANSITIONS из @crm/shared (Г18, 02.07.2026)
 * — раньше набор дублировал граф бэка вручную и мог молча разойтись; теперь
 * кнопка не покажется, если переход запрещён.
 * «Отказ» — через модал с причиной (window.prompt/confirm запрещены конвенцией).
 */
export function OrderStatusActions({ order }: { order: OrderDetailDto }) {
  const qc = useQueryClient()
  const [cancelOpen, setCancelOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const mutation = useMutation({
    mutationFn: (vars: { status: OrderStatus; note?: string }) => updateOrderStatus(order.id, vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['orders', order.id] })
      void qc.invalidateQueries({ queryKey: ['orders'] })
      setCancelOpen(false)
      setCancelReason('')
    },
    onError: (e) => toast.error(extractError(e)),
  })

  const allowed = ORDER_TRANSITIONS[order.status] ?? []

  // Кнопки ручных переходов; авто-переходы оплаты (PARTIALLY_PAID/PAID)
  // проставляет recompute на бэке — руками их не двигаем.
  const candidates: Array<{ to: OrderStatus; label: string; cls: string; run: () => void }> = [
    {
      to: 'CLOSED',
      label: 'Закрыть заказ',
      cls: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      run: () => mutation.mutate({ status: 'CLOSED', note: 'Заказ закрыт' }),
    },
    {
      to: 'AWAITING_PAYMENT',
      label: 'Ждём оплату',
      cls: 'bg-indigo-600 hover:bg-indigo-700 text-white',
      run: () => mutation.mutate({ status: 'AWAITING_PAYMENT' }),
    },
    {
      to: 'CANCELLED',
      label: 'Отказ',
      cls: 'bg-red-100 hover:bg-red-200 text-red-700',
      run: () => setCancelOpen(true),
    },
    {
      to: 'ARCHIVED',
      label: 'В архив',
      cls: 'bg-gray-200 hover:bg-gray-300 text-gray-700',
      run: () => mutation.mutate({ status: 'ARCHIVED', note: 'Перенесён в архив' }),
    },
  ]
  // «В архив» показываем только из терминальных статусов (CLOSED/CANCELLED) —
  // из рабочих матрица позволяет, но UX ведёт через отказ/закрытие.
  const buttons = candidates.filter(
    (b) =>
      allowed.includes(b.to) &&
      (b.to !== 'ARCHIVED' || order.status === 'CLOSED' || order.status === 'CANCELLED'),
  )

  if (buttons.length === 0) return null

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {buttons.map((b) => (
          <button
            key={b.to}
            className={`rounded px-3 py-1.5 text-sm font-medium ${b.cls} disabled:opacity-60`}
            onClick={b.run}
            disabled={mutation.isPending}
          >
            {b.label}
          </button>
        ))}
      </div>

      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl">
            <h3 className="mb-1 text-lg font-semibold">Отказ по заказу</h3>
            {order.paidSum > 0 && (
              <p className="mb-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                По заказу уже зафиксированы оплаты на {formatMoney(order.paidSum)}. При отказе
                платежи останутся в истории заказа (возврат оформляется отдельно вне CRM).
              </p>
            )}
            <label className="mb-3 block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Причина отказа</span>
              <textarea
                className="w-full rounded border border-gray-300 px-2 py-1.5"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                className="rounded-lg px-3 py-1.5 text-sm text-gray-600"
                onClick={() => setCancelOpen(false)}
              >
                Отмена
              </button>
              <button
                className="rounded-lg bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
                onClick={() => mutation.mutate({ status: 'CANCELLED', note: cancelReason.trim() })}
                disabled={mutation.isPending || !cancelReason.trim()}
              >
                Подтвердить отказ
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
